import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  FaHome, FaBox, FaCreditCard, FaHeadset, FaCog, FaBars, 
  FaTimes, FaUsers, FaUserCircle, FaHistory, FaSignOutAlt, 
  FaEnvelope, FaPhoneAlt, FaChartLine 
} from 'react-icons/fa';
import { RiShoppingBag3Line } from 'react-icons/ri';
import './Sidebar.scss';

function Sidebar({ collapsed, toggleSidebar }) {
  const location = useLocation();
  const { user, isAuthenticated } = useSelector(state => state.auth);
  const isAdmin = user?.role === 'admin' || user?.isAdmin === true || user?.userRole === 'admin';
  
  // Get user initials for avatar fallback
  const getUserInitials = () => {
    if (!user?.name) return 'U';
    return user.name.split(' ')
      .map(name => name[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };
  
  // Base menu items for all users
  const baseMenuItems = [
    { title: 'Dashboard', icon: <FaHome />, path: '/dashboard' },
    { title: 'Purchase Packages', icon: <RiShoppingBag3Line />, path: '/packages' },
    { title: 'Purchase History', icon: <FaHistory />, path: '/purchases' },
    { title: 'Billing & Payments', icon: <FaCreditCard />, path: '/billing' },
    { title: 'My Services', icon: <FaBox />, path: '/services' },
    { title: 'Support', icon: <FaHeadset />, path: '/support' }
  ];
  
  // Additional items only for admins
  const adminOnlyItems = [
    { title: 'Sales Tracking', icon: <FaChartLine />, path: '/sales-tracking' },
  ];
  
  // Combine base menu with admin-only items if the user is an admin
  const menuItems = isAdmin ? [...baseMenuItems, ...adminOnlyItems] : baseMenuItems;

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <div className="logo">
          <Link to="/">
            <img 
              src="https://www.fredocloud.com/fredocloud-logo-white.svg" 
              alt="FredoCloud" 
              className="logo-image"
            />
          </Link>
        </div>
        <button className="toggle-btn" onClick={toggleSidebar}>
          {collapsed ? <FaBars /> : <FaTimes />}
        </button>
      </div>
      <nav className="sidebar-nav">
        <ul>
          {menuItems.map((item, index) => (
            <li key={index} className={location.pathname === item.path ? 'active' : ''}>
              <Link to={item.path}>
                <span className="icon">{item.icon}</span>
                {!collapsed && <span className="title">{item.title}</span>}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      {isAuthenticated && (
        <div className={`sidebar-footer ${collapsed ? 'collapsed' : ''}`}>
          <Link to="/profile" className="user-info">
            {collapsed ? (
              <div className="user-avatar-small">
                {user?.profilePicture ? (
                  <img src={user.profilePicture} alt="Profile" />
                ) : (
                  <div className="avatar-initials small">{getUserInitials()}</div>
                )}
                <span className="status-indicator"></span>
              </div>
            ) : (
              <>
                <div className="user-avatar">
                  {user?.profilePicture ? (
                    <img src={user.profilePicture} alt="Profile" />
                  ) : (
                    <div className="avatar-initials">{getUserInitials()}</div>
                  )}
                  <span className="status-indicator"></span>
                </div>
                <div className="user-details">
                  <div className="user-name-container">
                    <p className="user-name">{user?.name || 'User'}</p>
                    <span className={`user-badge ${isAdmin ? 'admin-badge' : 'client-badge'}`}>
                      {isAdmin ? 'Admin' : 'Client'}
                    </span>
                  </div>
                  <p className="user-email">{user?.email}</p>
                  {user?.phone && (
                    <p className="user-phone">
                      <FaPhoneAlt className="icon-small" /> {user.phone}
                    </p>
                  )}
                  {!collapsed && (
                    <div className="user-actions">
                      <Link to="/profile" className="user-action-btn">
                        <FaUserCircle /> Profile
                      </Link>
                      <Link to="/logout" className="user-action-btn">
                        <FaSignOutAlt /> Logout
                      </Link>
                    </div>
                  )}
                </div>
              </>
            )}
          </Link>
        </div>
      )}
    </aside>
  );
}

export default Sidebar; 